/**
 * Step definitions for the guided agent creation wizard.
 *
 * Each step produces a ChatMessage and maps the user's answer to a field
 * in the agent config. In Option B (LLM), these are replaced by LLM responses
 * but the fieldPath and validate contracts stay the same for the config accumulator.
 */

import { WORKSPACE_DEFAULT, modelChoiceOptions } from "../../../utils/agentModel";

const STEPS = [
  // ─── Basics ───────────────────────────────────────────
  {
    id: "welcome",
    question: "Let's create a new agent! What would you like to name it?",
    type: "text",
    fieldPath: "name",
    validate: (v) => {
      if (!v || v.trim().length < 1) return "Name is required.";
      if (v.length > 100) return "Name must be under 100 characters.";
      return null;
    },
    transform: (v) => v.trim(),
  },
  {
    id: "purpose",
    question: "Describe what this agent should do. This becomes its system prompt — the personality, role, and instructions it follows.",
    type: "textarea",
    fieldPath: "system_prompt",
    placeholder: "e.g. You are a friendly customer support agent for Acme Corp. Answer questions using only the provided knowledge base, never fabricate information, and escalate to a human agent if you cannot resolve the issue.",
    validate: (v) => {
      if (!v || v.trim().length < 10) return "Please provide at least a brief description (10+ characters).";
      if (v.length > 10000) return "System prompt must be under 10,000 characters.";
      return null;
    },
    transform: (v) => v.trim(),
  },
  {
    id: "channels",
    question: "Which channels should this agent be available on?",
    type: "multi-select",
    fieldPath: "channels",
    options: [
      { label: "Web Chat", value: "chat", icon: "\uD83C\uDF10" },
      { label: "WhatsApp", value: "whatsapp", icon: "\uD83D\uDCAC" },
      { label: "Voice", value: "voice", icon: "\uD83D\uDCDE" },
      { label: "SMS", value: "sms", icon: "\uD83D\uDCF1" },
      { label: "Email", value: "email", icon: "\u2709" },
    ],
    default: ["chat"],
    validate: (v) => (!v || v.length === 0) ? "Select at least one channel." : null,
  },

  // ─── LLM Configuration ────────────────────────────────
  {
    // Phase M (M4-2): one question — follow the workspace default (changes when
    // an admin re-ranks models) or pin a specific chat model. No pre-picked
    // model: the default answer is the workspace default.
    id: "model",
    question: "Which model should it use?",
    type: "single-select",
    fieldPath: "model_choice",
    optionsFrom: (config, context) =>
      modelChoiceOptions(context?.availableModels, context?.workspaceDefault || null)
        .map((o) => ({ label: o.label, value: o.value })),
    default: WORKSPACE_DEFAULT,
  },
  {
    id: "temperature",
    question: "How creative should the responses be?",
    type: "single-select",
    fieldPath: "temperature",
    options: [
      { label: "Precise (0.2)", value: 0.2, icon: "\uD83C\uDFAF" },
      { label: "Balanced (0.7)", value: 0.7, icon: "\u2696" },
      { label: "Creative (1.2)", value: 1.2, icon: "\uD83C\uDFA8" },
    ],
    default: 0.7,
  },
  {
    id: "max_tokens",
    question: "Maximum response length?",
    type: "single-select",
    fieldPath: "max_tokens",
    options: [
      { label: "Short (512)", value: 512 },
      { label: "Medium (2048)", value: 2048 },
      { label: "Long (4096)", value: 4096 },
      { label: "Maximum (8192)", value: 8192 },
    ],
    default: 2048,
  },

  // ─── Safety & Guardrails ──────────────────────────────
  {
    id: "guardrails",
    question: "Which safety guardrails should be enabled?",
    type: "multi-select",
    fieldPath: "_guardrails",
    options: [
      { label: "PII Detection", value: "pii", icon: "\uD83D\uDD12" },
      { label: "Injection Guard", value: "injection", icon: "\uD83D\uDEE1" },
      { label: "Output Filter", value: "output", icon: "\u2714" },
    ],
    default: ["pii", "injection", "output"],
    transform: (v) => v || [],
  },
  {
    id: "escalation",
    question: "Should this agent be able to escalate to a human?",
    type: "single-select",
    fieldPath: "_escalation_enabled",
    options: [
      { label: "Yes", value: true, icon: "\uD83D\uDE4B" },
      { label: "No", value: false, icon: "\u2716" },
    ],
    default: true,
  },

  // ─── Knowledge Base ───────────────────────────────────
  {
    id: "knowledge",
    question: "Enable knowledge base (RAG)? This lets the agent search your documents and data to ground its responses.",
    type: "single-select",
    fieldPath: "_knowledge_enabled",
    options: [
      { label: "Yes, enable RAG", value: true, icon: "\uD83D\uDCDA" },
      { label: "No, skip", value: false, icon: "\u2716" },
    ],
    default: true,
  },

  // ─── MCP Tool Servers ─────────────────────────────────
  {
    id: "tools_enabled",
    question: "Should this agent have access to external tools (MCP servers)? This lets it call APIs, query databases, and interact with external systems.",
    type: "single-select",
    fieldPath: "_tools_enabled",
    options: [
      { label: "Yes, enable tools", value: true, icon: "\uD83D\uDD17" },
      { label: "No tools needed", value: false, icon: "\u2716" },
    ],
    default: true,
  },
  {
    id: "tools_select",
    question: "Select which MCP tool servers this agent can access. Leave empty to allow all servers.",
    type: "multi-select",
    fieldPath: "_allowed_servers",
    // Dynamic — populated from context.mcpServers
    optionsFrom: (_config, context) => {
      const servers = context.mcpServers || [];
      if (servers.length === 0) return [{ label: "No MCP servers configured", value: "__none__", icon: "\u26A0" }];
      return servers.map((s) => ({
        label: s.name || s.server_id,
        value: s.server_id,
        icon: s.enabled ? "\u2705" : "\u26A0",
      }));
    },
    default: [],
    skipIf: (config) => !config._tools_enabled,
    allowEmpty: true,
    validate: () => null, // Empty is valid (means all servers)
  },

  // ─── Routing Intents ──────────────────────────────────
  {
    id: "routing_enabled",
    question: "Add routing intents? These are labels that let a multi-agent router direct incoming messages to this agent based on the user's intent (e.g., \"billing\", \"complaint\", \"sales\").",
    type: "single-select",
    fieldPath: "_routing_enabled",
    options: [
      { label: "Yes, add intents", value: true, icon: "\uD83D\uDCCD" },
      { label: "Skip", value: false, icon: "\u2716" },
    ],
    default: false,
  },
  {
    id: "routing_intents",
    question: "Enter the routing intent labels, separated by commas (e.g., billing, complaint, sales).",
    type: "text",
    fieldPath: "routing_intents",
    placeholder: "billing, complaint, sales",
    skipIf: (config) => !config._routing_enabled,
    validate: () => null,
    transform: (v) => v ? v.split(",").map((s) => s.trim()).filter(Boolean) : [],
  },

  // ─── Multi-Agent Orchestration ────────────────────────
  {
    id: "orchestration_enabled",
    question: "Set up multi-agent orchestration? This lets you chain multiple agents together — sequential pipelines, parallel fan-out, or conditional routing.",
    type: "single-select",
    fieldPath: "_orchestration_enabled",
    options: [
      { label: "Yes, add sub-agents", value: true, icon: "\uD83E\uDD16" },
      { label: "No, single agent", value: false, icon: "\u2716" },
    ],
    default: false,
  },
  {
    id: "orchestration_pattern",
    question: "Which orchestration pattern?",
    type: "single-select",
    fieldPath: "_orchestration_pattern",
    options: [
      { label: "Sequential Chain (A \u2192 B \u2192 C)", value: "sequential", icon: "\u2192" },
      { label: "Parallel Fan-Out (A + B, then merge)", value: "parallel", icon: "\u21C9" },
      { label: "Conditional Routing (route by intent)", value: "conditional", icon: "\u25C7" },
    ],
    default: "sequential",
    skipIf: (config) => !config._orchestration_enabled,
  },
  {
    id: "orchestration_agents",
    question: "Select the sub-agents to include in this workflow. These must be existing agents.",
    type: "multi-select",
    fieldPath: "_orchestration_agent_ids",
    optionsFrom: (_config, context) => {
      const agents = context.availableAgents || [];
      if (agents.length === 0) return [{ label: "No other agents available", value: "__none__", icon: "\u26A0" }];
      return agents.map((a) => ({
        label: `${a.name} (${a.status})`,
        value: a.id,
        icon: a.status === "live" ? "\uD83D\uDFE2" : "\u26AA",
      }));
    },
    default: [],
    skipIf: (config) => !config._orchestration_enabled,
    validate: (v, config) => {
      if (config._orchestration_enabled && (!v || v.length === 0 || v.includes("__none__")))
        return "Select at least one sub-agent.";
      return null;
    },
  },

  // ─── Priority & Default ───────────────────────────────
  {
    id: "is_default",
    question: "Should this be the default agent for your workspace? The default agent handles messages when no specific routing intent matches.",
    type: "single-select",
    fieldPath: "is_default",
    options: [
      { label: "Yes, make default", value: true, icon: "\u2B50" },
      { label: "No", value: false, icon: "\u2716" },
    ],
    default: false,
  },

  // ─── Confirm ──────────────────────────────────────────
  {
    id: "confirm",
    question: null,
    type: "confirm",
    fieldPath: null,
  },
];

export default STEPS;
